// src/oura/components/client/drawerFlow.ts
//
// What a staff sheet needs from its host to finish an action the house way
// (drawer specs v1.1, A1.4 and A1.5): whether the item is still in the group
// it was opened from, how many are left there, which one comes next, and how
// to move on or close. The host owns the sequence (the GroupFold's rows in
// on-screen order when the drawer opened); the sheet owns what it just did.
//
// The one rule: an action completes when the item has left its snapshot
// group on the next read. Until then the foot shows only its message. A
// write that finishes after the sheet has gone (the drawer closed, or moved
// on) says its result in a toast instead (useSheetVoice).

import { useEffect, useRef } from "react";
import { useSnackbar } from "@hook/use-snackbar";
import type { DrawerDone, DrawerFootMessage, DrawerSeqItem } from "@oura/ui";
import type { CreatePrefill } from "../docs/create/createModel";

export interface SheetFlow {
    /** The item is still in the group it was opened from. */
    inGroup: boolean;
    /** The group word, for "2 left in Needs us". */
    group: string;
    /** Live items in the snapshot other than this one. */
    left: number;
    /** Where Next goes: the next live item after this one, else the first before it. */
    next: DrawerSeqItem | null;
    onNext(): void;
    /** Closes the drawer. */
    onDone(): void;
    /** In place of "Nothing else needs us." for a group that is not ours (Waiting on client, Calls scheduled). */
    lastLine?: string;
    /** A write is running: the host disables previous and next. */
    onBusy?(busy: boolean): void;
    /** Open another item by its id: the doc a skill's re-run wrote into (#3983). Absent: the host cannot. */
    onOpenDoc?(id: string): void;
    /** Open Create with this prefill: a Word file's "Convert to a Google Doc" (#4307). Absent: the host cannot. */
    onCreate?(prefill: CreatePrefill): void;
    /** A sub-view (a question opened from a call or a pack): a finished action shows its message, never Next. */
    sub?: boolean;
}

/** The done foot once `said` is set and the item has left its group; null until then. */
export function flowDone(flow: SheetFlow, said: string | null): DrawerDone | null {
    if (!said || flow.inGroup || flow.sub) return null;
    return {
        said,
        left: flow.left,
        group: flow.group,
        next: flow.next,
        audience: "staff",
        onNext: flow.onNext,
        onDone: flow.onDone,
        lastLine: flow.lastLine,
    };
}

/** The foot's message: an error or a note first, else what was just done while the item is still in its group. */
export function flowMessage(done: DrawerDone | null, message: DrawerFootMessage | null, said: string | null): DrawerFootMessage | null {
    if (done) return null;
    if (message) return message;
    return said ? { tone: "success", text: said.replace(/[.!]*$/, ".") } : null;
}

/**
 * Where Next goes after a completing action: the next live item after the
 * current one in the snapshot, else the first live one before it (the
 * current was the last). Null when nothing else is live.
 */
export function nextLive(ids: readonly string[], currentId: string, titleOf: (id: string) => string | null | undefined): DrawerSeqItem | null {
    const at = ids.indexOf(currentId);
    const order = at < 0 ? [...ids] : [...ids.slice(at + 1), ...ids.slice(0, at)];
    for (const id of order) {
        if (id === currentId) continue;
        const title = titleOf(id);
        if (title != null) return { id, title };
    }
    return null;
}

/** The last line for a group that is not "Needs us": "Nothing else in Waiting on client." */
export function lastLineFor(group: string): string | undefined {
    return group === "Needs us" ? undefined : `Nothing else in ${group}.`;
}

/**
 * Where a sheet says what just happened: its own foot while it is on screen,
 * a toast once it has gone (the drawer closed, or moved to another item,
 * while the write ran). `said` is a completing action's words; `note` any
 * other message, an error included.
 */
export function useSheetVoice(setSaid: (text: string | null) => void, setMessage: (m: DrawerFootMessage | null) => void) {
    const snackbar = useSnackbar();
    const gone = useRef(false);
    useEffect(() => {
        gone.current = false;
        return () => {
            gone.current = true;
        };
    }, []);
    const plain = (text: DrawerFootMessage["text"]) => (typeof text === "string" ? text : "");
    return {
        said(text: string) {
            if (gone.current) snackbar.showSuccess(text);
            else setSaid(text);
        },
        note(m: DrawerFootMessage) {
            if (!gone.current) {
                setMessage(m);
                return;
            }
            const text = plain(m.text);
            if (!text) return;
            if (m.tone === "error") snackbar.showError(text);
            else snackbar.showSuccess(text);
        },
    };
}
